import * as crypto from 'crypto';
import { EventEmitter } from 'events';
import { Feature, FeatureCollection, GeoJsonTypes, LineString as GEOLineString, Point } from 'geojson';
import * as uniqid from 'uniqid';

interface IFrameNode {
  id: string;
  point: ILngLat;
  endPoint: boolean;
}

type Frame = IFrameNode[];

const getMiddle = (prevPoint: ILngLat, newxtPoint: ILngLat): ILngLat => {
  return [
    (prevPoint[0] + newxtPoint[0]) / 2,
    (prevPoint[1] + newxtPoint[1]) / 2,
  ];
};

const md5 = (v: any) => crypto.createHash('md5').update(v).digest('hex');

const getHash = (obj: any, type: GeoJsonTypes, encode: 'wgs84' | 'gcj02') => {
  const str = JSON.stringify(obj);
  const md5Hash = md5(str);
  const suffix = Buffer.from(str).toString('base64').slice(-10);
  return `${type}.${encode}.${md5Hash}.${suffix}`;
};

export default class LineString extends EventEmitter {
  public id = uniqid();

  private _frame: Frame = [];
  private _draggingNode?: IFrameNode;

  public get points() {
    return this._frame.filter(({ endPoint }) => endPoint).map(({ point }) => {
      return point as ILngLat;
    });
  }

  public get hash() {
    return getHash(this.geometry.coordinates, 'LineString', 'wgs84');
  }

  public get geometry(): GEOLineString {
    const points = this.points;
    return {
      type: 'LineString',
      coordinates: points,
    };
  }

  public get geojsonEditor(): FeatureCollection<Point> {
    return {
      type: 'FeatureCollection',
      features: this._frame.map<Feature<Point>>(({ id, point, endPoint }) => {
        return {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: point,
          },
          properties: {
            id,
            type: endPoint ? 0 : 1,
          },
        };
      }),
    };
  }

  public get geojsonLineString(): Feature<GEOLineString> {
    return {
      type: 'Feature',
      geometry: this.geometry,
      properties: {
        id: this.id,
      },
    };
  }

  constructor(points: ILngLat[]) {
    super();
    this._initFrame(points);
  }

  public _finishMove() {
    this._draggingNode = undefined;
  }

  public onMapDragStart(id: string) {
    const node = this._frame.find(frameNode => frameNode.id === id);
    if (node) {
      this._selectDraggingNode(node);
    }
  }

  public onMapDrag(lngLat: ILngLat) {
    if (this._draggingNode) {
      if (!this._draggingNode.endPoint) {
        this._addNode(this._draggingNode);
      }
      this._moveNode(lngLat);
    }
  }

  public onMapDragEnd() {
    this._finishMove();
  }

  public onMapRightClick(id: string) {
    if (this.points.length <= 2) {
      return;
    }
    const node = this._frame.find(frameNode => frameNode.id === id);
    if (node) {
      this._removeNode(node);
    }
  }

  private _selectDraggingNode(frameEndPoint: IFrameNode) {
    this._draggingNode = frameEndPoint;
  }

  private _addNode(frameNode: IFrameNode) {
    const index = this._frame.indexOf(frameNode);
    frameNode.endPoint = true;
    const prevPoint = this._frame[index - 1];
    const nextPoint = this._frame[index + 1];
    this._frame.splice(index + 1, 0, {
      id: uniqid(),
      endPoint: false,
      point: getMiddle(frameNode.point, nextPoint.point),
    });
    this._frame.splice(index, 0, {
      id: uniqid(),
      endPoint: false,
      point: getMiddle(prevPoint.point, frameNode.point),
    });
    this._triggerUpdated();
  }

  private _removeNode(frameEndpoint: IFrameNode) {
    const index = this._frame.indexOf(frameEndpoint);
    if (index === 0) {
      return;
    }
    const prevNode = this._frame[index - 1];
    const nextNode = this._frame[index + 1];
    const prevPoint = this._frame[index - 2];
    const nextPoint = this._frame[index + 2];
    this._frame.splice(this._frame.indexOf(prevNode), 1);
    this._frame.splice(this._frame.indexOf(nextNode), 1);
    this._frame.splice(this._frame.indexOf(frameEndpoint), 1);
    this._frame.splice(this._frame.indexOf(prevPoint) + 1, 0, {
      id: uniqid(),
      endPoint: false,
      point: getMiddle(prevPoint.point, nextPoint.point),
    });
    this._triggerUpdated();
  }

  private _moveNode(lngLat: ILngLat) {
    if (!this._draggingNode) {
      return;
    }
    this._draggingNode.point = lngLat;
    const index = this._frame.indexOf(this._draggingNode);
    const prevNode = this._frame[index - 1];
    const nextNode = this._frame[index + 1];
    const prevPoint = this._frame[index - 2];
    const nextPoint = this._frame[index + 2];
    if (prevNode) {
      prevNode.point = getMiddle(prevPoint.point, lngLat);
    }
    if (nextNode) {
      nextNode.point = getMiddle(lngLat, nextPoint.point);
    }
    this._triggerUpdated();
  }

  private _initFrame(points: ILngLat[]) {
    const frame: Frame = [];
    points.reduce((prevPoint, nextPoint) => {
      frame.push({
        id: uniqid(),
        point: prevPoint,
        endPoint: true,
      }, {
        id: uniqid(),
        point: getMiddle(prevPoint, nextPoint),
        endPoint: false,
      });
      return nextPoint;
    });
    frame.push({
      id: uniqid(),
      point: points[points.length - 1],
      endPoint: true,
    });
    this._frame = frame;
  }

  private _triggerUpdated() {
    this.emit('updated');
  }

}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1-0!./src/classes/loaders/LineString.ts